import { LitElement, html, css } from 'lit'
import { visuallyHidden } from './lib/styles/mixins'

class HHQuantityInput extends LitElement {
  static properties = {
    key: {},
    label: {},
    initial: {
      type: Number,
    },
    _value: {
      state: true,
    },
    product: {
      type: String,
    },
    value: {
      type: String,
    },
    max: {
      type: Number,
      reflect: true
    },
    min: {
      type: Number,
      reflect: true
    },
    id: {
      type: String,
    },
    title: {
      type: String,
    },
  }

  constructor() {
    super()

    this.broadcast = this.broadcast.bind(this)
  }

  connectedCallback() {
    super.connectedCallback()
    this._value = this.initial
    this.id = this.product ?? this.key
  }

  broadcast() {
    const $input = this.querySelector('[slot="input"]')
    $input && ($input.value = this._value)
    this.renderRoot.querySelector('.quantity-input__input').value = this._value
    this.dispatchEvent(
      new CustomEvent('changeQuantity', {
        bubbles: true,
        composed: true,
        detail: {
          key: this.key,
          value: this._value,
          min: this.min,
          max: this.max
        },
      })
    )
  }

  increment() {
    if (typeof this.max !== 'undefined' && this._value >= this.max) {
      this.dispatchReachedMaxQuantity()
      return;
    }
    this._value += 1
    this.broadcast()
  }

  dispatchReachedMinQuantity() {
    this.dispatchEvent(
      new CustomEvent('reachedMinQuantity', {
        bubbles: true,
        composed: true,
        detail: {
          product: this.id,
          key: this.key,
          value: this._value,
        },
      })
    )
  }

  dispatchReachedMaxQuantity() {
    this.dispatchEvent(
      new CustomEvent('reachedMaxQuantity', {
        bubbles: true,
        composed: true,
        detail: {
          product: this.id,
          key: this.key,
          value: this._value,
        },
      })
    )
  }

  decrement() {
    if (this._value <= this.computedMin) {
      this.dispatchReachedMinQuantity()
      return
    }
    this._value -= 1
    this.broadcast()
  }

  override(e) {
    let value = Math.max(this.computedMin, e.target.value)
    if (typeof this.max !== 'undefined') {
      value = Math.min(value, this.max)
    }
    this._value = value
    this.broadcast()
    if (typeof this.max !== 'undefined' && e.target.value >= this.max) {
      this.dispatchReachedMaxQuantity()
    }
    if (e.target.value <= this.computedMin) {
      this.dispatchReachedMinQuantity()
    }
  }

  get increaseButton() {
    const slot = this.shadowRoot.querySelector('slot[name="increase"]')
    return slot.assignedElements()[0]
  }

  get decreaseButton() {
    const slot = this.shadowRoot.querySelector('slot[name="decrease"]')
    return slot.assignedElements()[0]
  }

  get computedMin () {
    return this.min ?? 0
  }

  render() {
    return html`
      <div class="wrapper">
        <slot @click="${this.decrement}" name="decrease"></slot>
        <label class="visually-hidden" for="Quantity-${this.id}">
          ${this.label | 'Quantity'}
        </label>
        <slot name="input"></slot>
        <input
          id="Quantity-${this.id}"
          class="input quantity-input__input"
          type="number"
          name="updates[]"
          .value="${this._value}"
          @change="${this.override}"
          .min="${this.computedMin}"
          .max="${this.max}"
        />
        <slot @click="${this.increment}" name="increase"></slot>
      </div>
    `
  }

  static styles = [
    visuallyHidden,
    css`
      :host {
        --hh-button-width: 1.4rem;
        --hh-color: var(--hh-color-subdued);
        --hh-max-width: max(6vw, 90px);
      }
      .wrapper {
        border: 0.1rem solid var(--hh-color-grey-mineral, #000);
        color: var(--hh-color, #000);
        background-color: var(--hh-background, transparent);
        max-width: var(--hh-max-width, 10rem);
        width: fit-content;
        position: relative;
        display: flex;
        padding: var(--hh-padding, 0 0.5rem);
      }

      .input {
        color: currentColor;
        background-color: var(--hh-background, transparent);
        font: var(--hh-font);
        text-align: center;
        background-color: transparent;
        border: 0;
        min-height: var(--hh-height, 2rem);
        min-width: 20px;
        max-width: max(2vw, 30px);
        width: 100%;
        flex-grow: 1;
        -webkit-appearance: none;
        appearance: none;
        -moz-appearance: textfield;
      }

      ::slotted(button) {
        width: var(--hh-button-width, 2.5rem);
        flex-shrink: 0;
        border: 0;
        background-color: transparent;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--hh-color, #000);
        transition: color var(--hh-duration-default);
        padding: 0;
      }

      ::slotted(button:hover) {
        color: var(--hh-color-honey-two);
      }

      .input:-webkit-autofill,
      .input:-webkit-autofill:hover,
      .input:-webkit-autofill:active {
        box-shadow: 0 0 0 10rem var(--hh-color-background) inset !important;
        -webkit-box-shadow: 0 0 0 10rem var(--hh-color-background) inset !important;
      }

      .input::-webkit-outer-spin-button,
      .input::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
    `,
  ]
}

customElements.define('hh-quantity-input', HHQuantityInput)
